import React from "react";

import { PageMode } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.constants";
import { capitalizeFirstLetter } from "@/mk2/apps/strollers/helpers";
import { strollersSearchByTypeUrl, strollersSearchUrl } from "@/mk2/apps/strollers/urls";
import { Link } from "@/mk2/components/Link";
import { url } from "@/mk2/helpers/urls";
import { StrollerEntity } from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    STROLLER_MAIN_FEATURES,
    STROLLER_MAIN_FEATURE_MAXIMUM_LOAD_PER_CHILD_IN_kg,
    STROLLER_MAIN_FEATURE_STROLLER_FOR_PARENT_BY_HEIGHT_TALL,
    STROLLER_MAIN_FEATURE_UNFOLDED_DIMENSIONS_IN_cm_WIDTH,
    STROLLER_MAIN_FEATURE_WEIGHT_IN_kg,
    STROLLER_SEARCH_FORM_VERY_GOOD_FOLDING,
    STROLLER_TYPE_CHECKED_BAGGAGE,
    STROLLER_TYPE_CITY_TERRAIN,
    STROLLER_TYPE_COUNTRYSIDE_TERRAIN,
    STROLLER_TYPE_HARD_TERRAIN,
    STROLLER_TYPE_SPORT_RUNNING,
} from "@/mk/autogenerated/translation";

import styles from "./MainFeaturesPills.mscss";

interface OwnProps {
    stroller: StrollerEntity;
}

type Props = OwnProps;

export class MainFeaturesPills extends React.Component<Props> {
    public render() {
        const { stroller } = this.props;

        const pillsProps = [];
        const CATEGORY_FEATURES = {
            hard_terrain: [STROLLER_TYPE_HARD_TERRAIN, "hard-terrain"],
            city_terrain: [STROLLER_TYPE_CITY_TERRAIN, "city-terrain"],
            countryside_terrain: [STROLLER_TYPE_COUNTRYSIDE_TERRAIN, "countryside-terrain"],
            sport_running: [STROLLER_TYPE_SPORT_RUNNING, "sport-running"],
            checked_baggage: [STROLLER_TYPE_CHECKED_BAGGAGE, "checked-baggage"],
        };
        for (const categoryFeature of Object.keys(CATEGORY_FEATURES)) {
            if (stroller.features[categoryFeature] === true) {
                pillsProps.push({
                    link: url(strollersSearchByTypeUrl, {
                        pageMode: PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_TYPE,
                        strollerType: CATEGORY_FEATURES[categoryFeature][1],
                    }),
                    label: CATEGORY_FEATURES[categoryFeature][0],
                });
                break;
            }
        }

        if (stroller.features.weight_in_kg) {
            pillsProps.push({
                link: url(
                    strollersSearchUrl,
                    { pageMode: PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY },
                    {
                        ...(stroller.features.weight_in_kg <= 10
                            ? { weight_is_low: true }
                            : { weight_in_kg: Math.ceil(stroller.features.weight_in_kg) }),
                    },
                ),
                label: interpolate(STROLLER_MAIN_FEATURE_WEIGHT_IN_kg, {
                    kg: stroller.features.weight_in_kg,
                }),
            });
        }
        if (stroller.features.maximum_load_per_child_in_kg) {
            let maximumLoadPerChildInKgRange;
            const MAXIMUM_LOAD_PER_CHILD_IN_KG_RANGES = {
                "0-15": [0, 15],
                "0-22": [0, 22],
                "22-99": [22, 99],
            };
            for (const key in MAXIMUM_LOAD_PER_CHILD_IN_KG_RANGES) {
                if (
                    MAXIMUM_LOAD_PER_CHILD_IN_KG_RANGES[key][0] <=
                        stroller.features.maximum_load_per_child_in_kg &&
                    stroller.features.maximum_load_per_child_in_kg <=
                        MAXIMUM_LOAD_PER_CHILD_IN_KG_RANGES[key][1]
                ) {
                    maximumLoadPerChildInKgRange = key;
                    break;
                }
            }

            pillsProps.push({
                link: url(
                    strollersSearchUrl,
                    { pageMode: PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY },
                    {
                        maximum_load_per_child_in_kg_range: maximumLoadPerChildInKgRange,
                        availability: "available",
                    },
                ),
                label: interpolate(STROLLER_MAIN_FEATURE_MAXIMUM_LOAD_PER_CHILD_IN_kg, {
                    kg: stroller.features.maximum_load_per_child_in_kg,
                }),
            });
        }
        if (stroller.features.unfolded_dimensions_in_cm) {
            pillsProps.push({
                link: url(
                    strollersSearchUrl,
                    { pageMode: PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY },
                    {
                        unfolded_dimensions_in_cm_width_max: Math.ceil(
                            stroller.features.unfolded_dimensions_in_cm.width,
                        ),
                        availability: "available",
                    },
                ),
                label: interpolate(STROLLER_MAIN_FEATURE_UNFOLDED_DIMENSIONS_IN_cm_WIDTH, {
                    cm: stroller.features.unfolded_dimensions_in_cm.width,
                }),
            });
        }
        if (stroller.features.very_good_folding) {
            pillsProps.push({
                link: url(
                    strollersSearchUrl,
                    { pageMode: PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY },
                    {
                        very_good_folding: true,
                        availability: "available",
                    },
                ),
                label: STROLLER_SEARCH_FORM_VERY_GOOD_FOLDING,
            });
        }
        if (stroller.features.stroller_for_parent_by_height === "height_gt_170") {
            pillsProps.push({
                link: url(
                    strollersSearchUrl,
                    { pageMode: PageMode.SHOW_SEARCH_RESULTS_FILTER_BY_QUERY },
                    {
                        stroller_for_parent_by_height: "height_gt_170",
                        availability: "available",
                    },
                ),
                label: STROLLER_MAIN_FEATURE_STROLLER_FOR_PARENT_BY_HEIGHT_TALL,
            });
        }

        if (!pillsProps.length) {
            return null;
        }

        return (
            <div className={styles.MainFeaturesPills}>
                <h2 className={styles.MainFeaturesPills__headline}>{STROLLER_MAIN_FEATURES}</h2>
                <div>
                    {pillsProps.map((pillProps, idx) => {
                        return <MainFeaturesPill key={idx} {...pillProps} />;
                    })}
                </div>
            </div>
        );
    }
}

interface MainFeaturesPillProps {
    label: string;
    link: string;
}

const MainFeaturesPillComponent: React.FunctionComponent<MainFeaturesPillProps> = ({
    label,
    link,
}) => (
    <Link to={link} className={styles.MainFeaturesPill}>
        {capitalizeFirstLetter(label)}
    </Link>
);

MainFeaturesPillComponent.displayName = "MainFeaturesPill";

const MainFeaturesPill = React.memo<MainFeaturesPillProps>(MainFeaturesPillComponent);
